import {isEngineFormat, normalizeSections, sourceIsCited} from "./engineCitations";
import {
    createSlice,
    createEntityAdapter,
    createSelector,
    PayloadAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import {chatApi} from "./chatApi";
import {
    RagSourceMessage,
    ChatStopReason,
    FollowUpSuggestion,
    isAssistantMessage,
    MessageWithMetadata,
    SourceInfo,
    AgentThought,
    AgentActivity,
    Narration,
    PluginActivity,
    RelatedToolsMessage,
    EphemeralSearch,
    PostSearchState,
    ListSource,
    ArtifactMessage,
    StreamedArtifactMessage,
    StreamErrorMessage,
    LowScoreMessage,
    NoAnswerMessage,
    NoSourcesMessage,
    ProffCompanyCard,
} from "../../types";
import {parsePartialJson} from "./utils/parsePartialJson";
import {llmBadJsonToGoodJson} from "./utils";
import {AppDispatch, RootState} from "../../app/store";

export interface ThreadState {
    id: string;
    realThreadId?: string;
    streamId?: string;
    name?: string;
    messages: MessageWithMetadata[];
    lastUpdated?: string;
    regenerationCount: number;

    isStreaming: boolean;

    error?: StreamErrorMessage;
    rateLimitCount: number;
    stopReason?: ChatStopReason;
    lowScoreMessage?: LowScoreMessage;
    noAnswerMessage?: NoAnswerMessage;
    noSourcesMessage?: NoSourcesMessage;

    ephemeralSearch: EphemeralSearch | null;
    postSearch: PostSearchState | null;

    /** Company cards referenced by inline `company:<orgNr>` links, keyed by orgNr. */
    proffCompanies?: Record<string, ProffCompanyCard>;
}

const threadsAdapter = createEntityAdapter<ThreadState>();

type ExtraState = {
    activeThreadId: string | null;
    completedBackgroundStreams: string[];
};

const initialState = threadsAdapter.getInitialState<ExtraState>({
    activeThreadId: null,
    completedBackgroundStreams: [],
});

// A message with no text and no artifact shows the user an empty answer bubble.
export const hasNoAnswer = (message: MessageWithMetadata) =>
    message.content.length === 0 && !message.artifacts?.length;

// These stop reasons need no report. The turn ended for a known reason, or the
// code that found the fault reported it already. Every other stop reason gets a
// report, including a reason that we do not know yet.
const explainedStopReasons = new Set<ChatStopReason | undefined>([
    ChatStopReason.UnknownError,
    ChatStopReason.NetworkError,
    ChatStopReason.ContentFilter,
    ChatStopReason.Aborted,
]);

// An assistant message with no usable answer fails in one of two ways, and the
// two faults need different names. An empty turn made no content at all.
// Content that will not parse is a citation problem.
const reportUnusableAnswer = (
    message: MessageWithMetadata,
    stopReason?: ChatStopReason
) => {
    const hasText = message.content.length > 0;
    const isArtifactOnlyAnswer = !hasText && !!message.artifacts?.length;
    if (isArtifactOnlyAnswer) {
        return;
    }

    const err = new Error(
        hasText
            ? "Citation JSON parse failed"
            : "Assistant answer finished with no content"
    );
    console.warn(err.message);
    Sentry.captureException(err, {
        tags: {feature: hasText ? "chat.citation-parse" : "chat.empty-answer"},
        extra: {
            stopReason,
            contentLength: message.content.length,
            contentSample: message.content.slice(0, 500),
        },
    });
};

const emptyThread = (id: string): ThreadState => ({
    id,
    messages: [],
    regenerationCount: 0,
    isStreaming: false,
    rateLimitCount: 0,
    ephemeralSearch: null,
    postSearch: null,
});

const threadsSlice = createSlice({
    name: "threads",
    initialState,
    reducers: {
        createThread(
            state,
            action: PayloadAction<{
                id: string;
                streamId?: string;
                initialMessages?: MessageWithMetadata[];
                realThreadId?: string;
                ephemeralSearch?: EphemeralSearch | null;
            }>
        ) {
            const {id, streamId, initialMessages, realThreadId, ephemeralSearch} =
                action.payload;
            threadsAdapter.upsertOne(state, {
                ...emptyThread(id),
                streamId,
                realThreadId,
                messages: initialMessages ?? [],
                isStreaming: true,
                ephemeralSearch: ephemeralSearch ?? null,
            });
        },
        setActiveThread(state, action: PayloadAction<string | null>) {
            state.activeThreadId = action.payload;
            if (action.payload) {
                const idx = state.completedBackgroundStreams.indexOf(
                    action.payload
                );
                if (idx !== -1) {
                    state.completedBackgroundStreams.splice(idx, 1);
                }
            }
        },
        setRealThreadId(
            state,
            action: PayloadAction<{id: string; realThreadId: string}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.realThreadId = action.payload.realThreadId;
            }
        },
        setStreamId(
            state,
            action: PayloadAction<{id: string; streamId: string | undefined}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.streamId = action.payload.streamId;
            }
        },
        setIsStreaming(
            state,
            action: PayloadAction<{id: string; isStreaming: boolean}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.isStreaming = action.payload.isStreaming;
            }
        },
        addMessage(
            state,
            action: PayloadAction<{id: string; message: MessageWithMetadata}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.messages.push(action.payload.message);
            }
        },
        replaceMessage(
            state,
            action: PayloadAction<{id: string; content: string}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.content = action.payload.content;
            }
        },
        finishAssistantMessage(
            state,
            action: PayloadAction<{
                id: string;
                text: string;
                stopReason?: ChatStopReason;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;

            const last = entity.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                // An empty final text must not erase content that the stream
                // already delivered.
                if (action.payload.text.length > 0) {
                    last.content = action.payload.text;
                }
                const answer = llmBadJsonToGoodJson(last.content);
                if (answer) {
                    const citations = answer.citations;
                    if (citations && last.sources) {
                        const citedIds = citations.filter(
                            (c): c is string => typeof c === "string"
                        );
                        const citedIndices = citations.filter(
                            (c): c is number => typeof c === "number"
                        );
                        last.consideredSources = last.sources.filter(
                            (source: SourceInfo) =>
                                isEngineFormat(answer, answer.content)
                                    ? !sourceIsCited(source, citedIds)
                                    : !citedIndices.includes(source.index)
                        );
                    }
                } else if (
                    !explainedStopReasons.has(action.payload.stopReason)
                ) {
                    reportUnusableAnswer(last, action.payload.stopReason);
                }
            }

            entity.isStreaming = false;
            entity.stopReason = action.payload.stopReason;

            // PostSearch is start-only: the `process`/postsearch frame opens
            // the searching state and `postsearchresult` closes it, but that
            // result event does NOT fire when PostSearch finds nothing. So if
            // we're still searching when the stream ends, clear the pending
            // state here — otherwise the empty-results case hangs the tabs.
            if (entity.postSearch?.status === "searching") {
                entity.postSearch = null;
            }

            if (state.activeThreadId !== entity.id) {
                if (!state.completedBackgroundStreams.includes(entity.id)) {
                    state.completedBackgroundStreams.push(entity.id);
                }
            }
        },
        rateLimitReset(state, action: PayloadAction<{id: string}>) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            entity.isStreaming = true;
            const last = entity.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.sources = [];
                last.content = "";
            }
        },
        addSourceToLastMessage(
            state,
            action: PayloadAction<{id: string; payload: RagSourceMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                const p = action.payload.payload;
                last.sources = last.sources ?? [];
                // The backend streams a source once per section batch: the first event
                // carries the sections of the first search, later events only the
                // sections the client has not seen. Merge them by index so the live
                // message ends with the same sections per source as the stored one.
                const known = last.sources.find((s) => s.index === p.index);
                if (known) {
                    const incoming = normalizeSections(p.sections) ?? [];
                    const held = new Set((known.sections ?? []).map((s) => s.id));
                    known.sections = [
                        ...(known.sections ?? []),
                        ...incoming.filter((s) => !held.has(s.id)),
                    ];
                    return;
                }
                last.sources.push({
                    index: p.index,
                    id: p.topicid ?? "",
                    // ChartBlock reads topicId to attribute a Proff company chart.
                    // History-loaded sources carry it from the backend already;
                    // a source that arrives live over SSE needs it set here too.
                    topicId: p.topicid,
                    // Let a PDF source open in the viewer, at the hit's position.
                    resourceTypeid: p.resourcetypeid,
                    pageIndex: p.pageindex,
                    y: p.y,
                    name: p.title ?? "",
                    segmentId: p.segmentid ?? "",
                    segmentName: p.segmentName ?? "",
                    type: p.topictype ?? "",
                    description: p.description ?? "",
                    url: p.url ?? "",
                    sections: normalizeSections(p.sections),
                });
            }
        },
        upsertProffCompany(
            state,
            action: PayloadAction<{id: string; payload: ProffCompanyCard}>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            entity.proffCompanies = entity.proffCompanies ?? {};
            entity.proffCompanies[action.payload.payload.orgNr] =
                action.payload.payload;
        },
        addRelatedToolsToLastMessage(
            state,
            action: PayloadAction<{id: string; payload: RelatedToolsMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                const p = action.payload.payload;
                last.relatedTools = last.relatedTools ?? [];
                last.relatedTools.push({
                    index: p.index,
                    id: p.topicid ?? "",
                    title: p.title ?? "",
                    type: p.topictype ?? "",
                    url: p.url ?? "",
                    description: p.description ?? "",
                    score: p.score ?? 0,
                    isFile: p.isFile ?? false,
                    updatedAt: p.updatedAt ?? "",
                });
            }
        },
        addArtifactToLastMessage(
            state,
            action: PayloadAction<{
                id: string;
                payload: ArtifactMessage;
                latencyMs?: number;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                const p = action.payload.payload;
                last.artifacts = last.artifacts ?? [];
                const existing = last.artifacts.findIndex(
                    (a) => a.id === p.id
                );
                const artifact = {
                    id: p.id,
                    type: p.artifactType,
                    schemaVersion: p.schemaVersion,
                    source: "ai" as const,
                    payload: p.payload,
                    createdAt: new Date().toISOString(),
                    latencyMs: action.payload.latencyMs,
                };
                if (existing >= 0) {
                    last.artifacts[existing] = artifact;
                } else {
                    last.artifacts.push(artifact);
                }
            }
        },
        handleStreamedArtifact(
            state,
            action: PayloadAction<{id: string; payload: StreamedArtifactMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (!last || !isAssistantMessage(last)) return;

            last.artifacts = last.artifacts ?? [];
            const {id: artifactId, artifactType, schemaVersion, delta} =
                action.payload.payload;

            if (artifactType) {
                const existing = last.artifacts.findIndex(
                    (a) => a.id === artifactId
                );
                const artifact = {
                    id: artifactId,
                    type: artifactType,
                    schemaVersion: schemaVersion ?? "1.0",
                    source: "ai" as const,
                    payload: {},
                    createdAt: new Date().toISOString(),
                };
                if (existing >= 0) {
                    last.artifacts[existing] = artifact;
                } else {
                    last.artifacts.push(artifact);
                }
                return;
            }

            if (!delta) return;
            const artifact = last.artifacts.find((a) => a.id === artifactId);
            if (!artifact) return;

            const rawKey = "__streamingRawJson";
            const current = ((artifact as any)[rawKey] as string) || "";
            const updated = current + delta;
            (artifact as any)[rawKey] = updated;

            const parsed = parsePartialJson(updated);
            if (parsed !== null) {
                artifact.payload = parsed;
            }
        },
        setFollowUpSuggestionsOnLastMessage(
            state,
            action: PayloadAction<{
                id: string;
                suggestions: FollowUpSuggestion[];
            }>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.followUpSuggestions = action.payload.suggestions;
            }
        },
        setFeaturesOnLastMessage(
            state,
            action: PayloadAction<{id: string; features: string[]}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.features = action.payload.features;
            }
        },
        addAgentThought(
            state,
            action: PayloadAction<{id: string; payload: AgentThought}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.thoughts = last.thoughts ?? [];
                last.thoughts.push(action.payload.payload);
            }
        },
        addNarration(
            state,
            action: PayloadAction<{id: string; payload: Narration}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.narrations = last.narrations ?? [];
                last.narrations.push(action.payload.payload);
            }
        },
        addAgentActivity(
            state,
            action: PayloadAction<{id: string; payload: AgentActivity}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.activities = last.activities ?? [];
                last.activities.push(action.payload.payload);
            }
        },
        addPluginActivity(
            state,
            action: PayloadAction<{id: string; payload: PluginActivity}>
        ) {
            const entity = state.entities[action.payload.id];
            const last = entity?.messages[entity.messages.length - 1];
            if (last && isAssistantMessage(last)) {
                last.pluginActivities = last.pluginActivities ?? [];
                last.pluginActivities.push(action.payload.payload);
            }
        },
        addStreamError(
            state,
            action: PayloadAction<{id: string; error: StreamErrorMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            entity.error = action.payload.error;
            entity.rateLimitCount += 1;
        },
        clearThreadError(state, action: PayloadAction<{id: string}>) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            entity.error = undefined;
            entity.stopReason = undefined;
        },
        setStopReason(
            state,
            action: PayloadAction<{id: string; stopReason: ChatStopReason}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.stopReason = action.payload.stopReason;
            }
        },
        addLowScoreMessage(
            state,
            action: PayloadAction<{id: string; message: LowScoreMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.lowScoreMessage = action.payload.message;
            }
        },
        addNoAnswerMessage(
            state,
            action: PayloadAction<{id: string; message: NoAnswerMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.noAnswerMessage = action.payload.message;
            }
        },
        addNoSourcesMessage(
            state,
            action: PayloadAction<{id: string; message: NoSourcesMessage}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.noSourcesMessage = action.payload.message;
            }
        },
        clearKaiPlusSuggestionMessage(
            state,
            action: PayloadAction<{id: string; messageId: number}>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            if (entity.lowScoreMessage?.messageId === action.payload.messageId) {
                entity.lowScoreMessage = undefined;
            }
            if (
                entity.noAnswerMessage?.messageId === action.payload.messageId
            ) {
                entity.noAnswerMessage = undefined;
            }
            if (
                entity.noSourcesMessage?.messageId === action.payload.messageId
            ) {
                entity.noSourcesMessage = undefined;
            }
        },
        setEphemeralSearch(
            state,
            action: PayloadAction<{
                id: string;
                ephemeralSearch: EphemeralSearch | null;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.ephemeralSearch = action.payload.ephemeralSearch;
            }
        },
        setPostSearchPending(
            state,
            action: PayloadAction<{
                id: string;
                streamId: string;
                query?: string;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            // The `process`/postsearch frame that triggers this carries no
            // query, so fall back to the thread's most recent user message for
            // the "Search results for …" header shown while searching.
            const query =
                action.payload.query ??
                [...entity.messages].reverse().find((m) => m.role === "user")
                    ?.content ??
                "";
            entity.postSearch = {
                streamId: action.payload.streamId,
                query,
                status: "searching",
                tools: [],
                otherSources: [],
            };
        },
        setPostSearchResults(
            state,
            action: PayloadAction<{
                id: string;
                streamId: string;
                tools: ListSource[];
                otherSources: ListSource[];
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity || !entity.postSearch) return;
            if (entity.postSearch.streamId !== action.payload.streamId) return;
            const total =
                action.payload.tools.length + action.payload.otherSources.length;
            entity.postSearch = {
                ...entity.postSearch,
                tools: action.payload.tools,
                otherSources: action.payload.otherSources,
                status: total === 0 ? "empty" : "ready",
            };
        },
        setPostSearchError(
            state,
            action: PayloadAction<{id: string; streamId: string}>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity || !entity.postSearch) return;
            if (entity.postSearch.streamId !== action.payload.streamId) return;
            entity.postSearch.status = "error";
        },
        clearPostSearch(state, action: PayloadAction<{id: string}>) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.postSearch = null;
            }
        },
        // Populate PostSearch from the `postsearchresult` stream event. The
        // backend has already run the search and persisted it on the thread, so
        // we mark it persisted to ensure nothing tries to re-save.
        setPostSearchFromStream(
            state,
            action: PayloadAction<{
                id: string;
                query: string;
                tools: ListSource[];
                otherSources: ListSource[];
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            const total =
                action.payload.tools.length +
                action.payload.otherSources.length;
            entity.postSearch = {
                streamId: action.payload.id,
                query: action.payload.query,
                tools: action.payload.tools,
                otherSources: action.payload.otherSources,
                status: total === 0 ? "empty" : "ready",
                persisted: true,
            };
        },
        markPostSearchPersisted(state, action: PayloadAction<{id: string}>) {
            const entity = state.entities[action.payload.id];
            if (entity?.postSearch) {
                entity.postSearch.persisted = true;
            }
        },
        updateThreadName(
            state,
            action: PayloadAction<{id: string; name: string}>
        ) {
            const entity = state.entities[action.payload.id];
            if (entity) {
                entity.name = action.payload.name;
            }
        },
        updateThread(
            state,
            action: PayloadAction<{
                id: string;
                realThreadId: string;
                name: string;
                messages: MessageWithMetadata[];
                datestamp: string;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            entity.realThreadId = action.payload.realThreadId;
            entity.name = action.payload.name;
            entity.lastUpdated = action.payload.datestamp;
            entity.messages = [
                ...entity.messages.filter((m) => !!m.position),
                ...action.payload.messages,
            ];
        },
        removeMessagesFromIndex(
            state,
            action: PayloadAction<{id: string; position: number}>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            const index = entity.messages.findIndex(
                (m) => m.position === action.payload.position
            );
            if (index !== -1) {
                entity.messages.splice(index, entity.messages.length - index);
            }
        },
        endRejectedTurn(
            state,
            action: PayloadAction<{
                id: string;
                removeCount: number;
                restore: MessageWithMetadata[];
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            if (action.payload.removeCount > 0) {
                entity.messages.splice(-action.payload.removeCount);
                entity.messages.push(...action.payload.restore);
            }
            entity.isStreaming = false;
            entity.streamId = undefined;
            entity.stopReason = undefined;
        },
        clearCompletedStream(state, action: PayloadAction<string>) {
            const idx = state.completedBackgroundStreams.indexOf(
                action.payload
            );
            if (idx !== -1) {
                state.completedBackgroundStreams.splice(idx, 1);
            }
        },
        removeThread(state, action: PayloadAction<string>) {
            threadsAdapter.removeOne(state, action.payload);
            if (state.activeThreadId === action.payload) {
                state.activeThreadId = null;
            }
            const idx = state.completedBackgroundStreams.indexOf(
                action.payload
            );
            if (idx !== -1) {
                state.completedBackgroundStreams.splice(idx, 1);
            }
        },
        rateMessageLocal(
            state,
            action: PayloadAction<{
                id: string;
                messagePosition: number;
                rating: number;
                comment?: string;
            }>
        ) {
            const entity = state.entities[action.payload.id];
            if (!entity) return;
            const message = entity.messages[action.payload.messagePosition];
            if (message) {
                message.rating = action.payload.rating;
                message.feedback = action.payload.comment;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            chatApi.endpoints.getThread.matchFulfilled,
            (state, action) => {
                const payload = action.payload;
                if (!payload?.threadId) return;
                const existing = Object.values(state.entities).find(
                    (t) =>
                        t?.realThreadId === payload.threadId ||
                        t?.id === payload.threadId
                );
                if (existing?.isStreaming) return;
                // If we already streamed this thread to completion in this
                // session (stopReason is set), trust the local state — the
                // server may not have persisted the final answer yet, and
                // overwriting would wipe the message from the UI.
                if (existing?.stopReason !== undefined) return;

                const id = existing?.id ?? payload.threadId;
                const persistedPostSearch = payload.postSearch
                    ? ({
                          streamId: "",
                          query: payload.postSearch.query,
                          status:
                              payload.postSearch.tools.length +
                                  payload.postSearch.otherSources.length >
                              0
                                  ? "ready"
                                  : "empty",
                          tools: payload.postSearch.tools,
                          otherSources: payload.postSearch.otherSources,
                          persisted: true,
                      } satisfies PostSearchState)
                    : null;
                const seededCompanies = payload.proffCompanies?.length
                    ? Object.fromEntries(
                          payload.proffCompanies.map((c) => [c.orgNr, c])
                      )
                    : undefined;
                threadsAdapter.upsertOne(state, {
                    ...emptyThread(id),
                    ...(existing ?? {}),
                    id,
                    realThreadId: payload.threadId,
                    name: payload.name,
                    messages: payload.messages ?? [],
                    lastUpdated: payload.lastUpdated,
                    regenerationCount: payload.regenerationCount ?? 0,
                    isStreaming: false,
                    postSearch: existing?.postSearch ?? persistedPostSearch,
                    proffCompanies: seededCompanies
                        ? {...seededCompanies, ...(existing?.proffCompanies ?? {})}
                        : existing?.proffCompanies,
                });
            }
        );
        builder.addMatcher(
            chatApi.endpoints.rateMessage.matchFulfilled,
            (state, action) => {
                const {messagePosition, rating, comment} =
                    action.meta.arg.originalArgs;
                const threadIdArg = (action.meta.arg.originalArgs as any)
                    .threadId;
                const target = Object.values(state.entities).find(
                    (t) =>
                        t?.realThreadId === threadIdArg || t?.id === threadIdArg
                );
                if (!target) return;
                const message = target.messages[messagePosition];
                if (message) {
                    message.rating = rating;
                    message.feedback = comment;
                }
            }
        );
    },
});

export const {
    createThread,
    setActiveThread,
    setRealThreadId,
    setStreamId,
    setIsStreaming,
    addMessage,
    replaceMessage,
    finishAssistantMessage,
    rateLimitReset,
    addSourceToLastMessage,
    upsertProffCompany,
    addRelatedToolsToLastMessage,
    addArtifactToLastMessage,
    handleStreamedArtifact,
    setFollowUpSuggestionsOnLastMessage,
    setFeaturesOnLastMessage,
    addAgentThought,
    addAgentActivity,
    addNarration,
    addPluginActivity,
    addStreamError,
    clearThreadError,
    setStopReason,
    addLowScoreMessage,
    addNoAnswerMessage,
    addNoSourcesMessage,
    clearKaiPlusSuggestionMessage,
    setEphemeralSearch,
    setPostSearchPending,
    setPostSearchResults,
    setPostSearchError,
    clearPostSearch,
    setPostSearchFromStream,
    markPostSearchPersisted,
    updateThreadName,
    updateThread,
    removeMessagesFromIndex,
    endRejectedTurn,
    clearCompletedStream,
    removeThread,
} = threadsSlice.actions;

export default threadsSlice.reducer;

export const threadsSelectors = threadsAdapter.getSelectors(
    (state: RootState) => state.threads
);

export const selectActiveThreadId = (state: RootState) =>
    state.threads.activeThreadId;

export const selectActiveThread = (state: RootState): ThreadState | undefined => {
    const id = state.threads.activeThreadId;
    return id ? state.threads.entities[id] : undefined;
};

const EMPTY_MESSAGES: MessageWithMetadata[] = [];

export const selectActiveThreadMessages = (
    state: RootState
): MessageWithMetadata[] => selectActiveThread(state)?.messages ?? EMPTY_MESSAGES;

const EMPTY_PROFF_COMPANIES: Record<string, ProffCompanyCard> = {};

export const selectActiveThreadProffCompanies = (
    state: RootState
): Record<string, ProffCompanyCard> =>
    selectActiveThread(state)?.proffCompanies ?? EMPTY_PROFF_COMPANIES;

export const selectIsActiveThreadStreaming = (state: RootState): boolean =>
    selectActiveThread(state)?.isStreaming ?? false;

export const selectActiveStreamCount = (state: RootState): number =>
    threadsSelectors.selectAll(state).filter((t) => t.isStreaming).length;

export const selectStreamingThreadIds = createSelector(
    [threadsSelectors.selectAll],
    (threads) =>
        threads
            .filter((t) => t.isStreaming)
            .map((t) => t.realThreadId ?? t.id)
);

export const selectCompletedBackgroundStreams = (state: RootState): string[] =>
    state.threads.completedBackgroundStreams;

export const selectThreadByRealId = (
    state: RootState,
    threadId: string
): ThreadState | undefined =>
    threadsSelectors
        .selectAll(state)
        .find((t) => t.realThreadId === threadId || t.id === threadId);

export interface RejectedTurn {
    id: string;
    added: {position: number; role: MessageWithMetadata["role"]}[];
    removedTail: MessageWithMetadata[];
}

export const rollbackRejectedTurn =
    ({id, added, removedTail}: RejectedTurn) =>
    (dispatch: AppDispatch, getState: () => RootState) => {
        const entity = threadsSelectors.selectById(getState(), id);
        if (!entity) return;
        const tail = entity.messages.slice(-added.length);
        const endsWithTurn =
            tail.length === added.length &&
            tail.every(
                (m, i) =>
                    m.position === added[i].position &&
                    m.role === added[i].role
            );
        dispatch(
            endRejectedTurn({
                id,
                removeCount: endsWithTurn ? added.length : 0,
                restore: endsWithTurn ? removedTail : [],
            })
        );
        if (!endsWithTurn && entity.realThreadId) {
            dispatch(
                chatApi.endpoints.getThread.initiate(entity.realThreadId, {
                    forceRefetch: true,
                })
            );
        }
    };
